import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  WorkOutline as JobIcon,
  Computer as ComputerIcon,
  Code as CodeIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import api from '../api';
import { showSuccess, showError } from '../utils/sweetAlertConfig';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";

const steps = ['Select Job', 'Choose Assessment Type', 'Configure Assessment', 'Select Candidates'];

export default function AssignCandidatesModal({ open, onClose, onSuccess, jobs }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Step 1: Job Selection
  const [selectedJob, setSelectedJob] = useState('');

  // Step 2: Assessment Type
  const [assessmentType, setAssessmentType] = useState('digital_only');

  // Step 3: Assessment Configuration
  const [digitalExams, setDigitalExams] = useState([]);
  const [technicalTasks, setTechnicalTasks] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedTask, setSelectedTask] = useState('');

  // Step 4: Candidate Selection
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);

  useEffect(() => {
    if (open) {
      fetchExams();
      fetchTasks();
    }
  }, [open]);

  useEffect(() => {
    if (selectedJob && activeStep === 3) {
      fetchShortlistedCandidates(selectedJob);
    }
  }, [selectedJob, activeStep]);

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams');
      setDigitalExams(response.data);
    } catch (error) {
      console.error('Failed to fetch exams');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await api.get('/technical-tasks');
      setTechnicalTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks');
    }
  };

  const fetchShortlistedCandidates = async (jobId) => {
    setLoadingCandidates(true);
    try {
      const response = await api.get(`/assessment-management/shortlisted-candidates/${jobId}`);
      setCandidates(response.data);
    } catch (error) {
      showError('Failed to fetch shortlisted candidates');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedJob) {
      showError('Please select a job');
      return;
    }
    if (activeStep === 2) {
      if ((assessmentType === 'digital_only' || assessmentType === 'both') && !selectedExam) {
        showError('Please select a digital exam');
        return;
      }
      if ((assessmentType === 'technical_only' || assessmentType === 'both') && !selectedTask) {
        showError('Please select a technical task');
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleToggleCandidate = (candidateId) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    const availableCandidates = (candidates || []).filter(c => !c.has_assignment);
    if (selectedCandidates.length === availableCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(availableCandidates.map(c => c.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedCandidates.length === 0) {
      showError('Please select at least one candidate');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        job_id: selectedJob,
        candidate_ids: selectedCandidates,
        assessment_type: assessmentType,
        digital_assessment_id: selectedExam || null,
        technical_task_id: selectedTask || null,
      };

      await api.post('/assessment-management/assign', payload);
      showSuccess(`Assessment assigned to ${selectedCandidates.length} candidate(s) successfully!`);
      handleClose();
      onSuccess();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to assign assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedJob('');
    setAssessmentType('digital_only');
    setSelectedExam('');
    setSelectedTask('');
    setSelectedCandidates([]);
    setCandidates([]);
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <JobIcon /> Select Job Position
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Job Position</InputLabel>
              <Select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                label="Job Position"
              >
                {jobs && Array.isArray(jobs) && jobs.map((job) => (
                  <MenuItem key={job.job_id} value={job.job_id}>
                    {job.posting_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon /> Choose Assessment Type
            </Typography>
            <RadioGroup value={assessmentType} onChange={(e) => setAssessmentType(e.target.value)}>
              <Paper sx={{ p: 2, mb: 2, border: assessmentType === 'digital_only' ? `2px solid ${PRIMARY}` : '1px solid #e0e0e0' }}>
                <FormControlLabel
                  value="digital_only"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <ComputerIcon sx={{ color: PRIMARY }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>Digital Assessment Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Multiple choice questions with automatic scoring
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, mb: 2, border: assessmentType === 'technical_only' ? `2px solid ${PRIMARY}` : '1px solid #e0e0e0' }}>
                <FormControlLabel
                  value="technical_only"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <CodeIcon sx={{ color: PRIMARY }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>Technical Task Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Practical task with file submission and manual review
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, border: assessmentType === 'both' ? `2px solid ${PRIMARY}` : '1px solid #e0e0e0' }}>
                <FormControlLabel
                  value="both"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <AssignmentIcon sx={{ color: PRIMARY }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>Both Assessments</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Combination of digital questions and technical task
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Paper>
            </RadioGroup>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Configure Assessment</Typography>

            {(assessmentType === 'digital_only' || assessmentType === 'both') && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ComputerIcon fontSize="small" /> Select Digital Exam
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Digital Exam</InputLabel>
                  <Select
                    value={selectedExam}
                    onChange={(e) => setSelectedExam(e.target.value)}
                    label="Digital Exam"
                  >
                    {(digitalExams || [])
                      .filter(exam => exam.job_id === selectedJob || !exam.job_id)
                      .map((exam) => (
                        <MenuItem key={exam.exam_id} value={exam.exam_id}>
                          {exam.title} ({exam.questions_count || 0} questions, {exam.duration} min)
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {(assessmentType === 'technical_only' || assessmentType === 'both') && (
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CodeIcon fontSize="small" /> Select Technical Task
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Technical Task</InputLabel>
                  <Select
                    value={selectedTask}
                    onChange={(e) => setSelectedTask(e.target.value)}
                    label="Technical Task"
                  >
                    {(technicalTasks || [])
                      .filter(task => task.job_id === selectedJob)
                      .map((task) => (
                        <MenuItem key={task.task_id} value={task.task_id}>
                          {task.title} ({task.deadline_days} days deadline)
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon /> Select Candidates
              </Typography>
              <Button
                size="small"
                onClick={handleSelectAll}
                disabled={(candidates || []).filter(c => !c.has_assignment).length === 0}
              >
                {selectedCandidates.length === (candidates || []).filter(c => !c.has_assignment).length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            {loadingCandidates ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (candidates || []).length === 0 ? (
              <Alert severity="warning">No shortlisted candidates found for this job.</Alert>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {(candidates || []).map((candidate) => (
                  <ListItem
                    key={candidate.id}
                    dense
                    disabled={candidate.has_assignment}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedCandidates.includes(candidate.id)}
                        onChange={() => handleToggleCandidate(candidate.id)}
                        disabled={candidate.has_assignment}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {candidate.full_name}
                          {candidate.has_assignment && (
                            <Chip label="Already Assigned" size="small" color="warning" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block">{candidate.email}</Typography>
                          <Typography variant="caption" color="text.secondary">{candidate.candidate_id}</Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {selectedCandidates.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {selectedCandidates.length} candidate(s) selected
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Assign Assessment to Candidates
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#1a1635' } }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || selectedCandidates.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{ bgcolor: SUCCESS, '&:hover': { bgcolor: '#4a9c5a' } }}
          >
            Assign Assessment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
